<script setup>
// Painel de QR usado na criação e na reconexão. Pergunta o estado a cada
// 3 segundos, troca o QR quando a Uazapi renova e para ao conectar ou
// depois de 3 minutos.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import UazapiAPI from 'dashboard/api/uazapi';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inboxId: { type: Number, required: true },
  initialQrcode: { type: String, default: '' },
  showContinue: { type: Boolean, default: false },
});
const emit = defineEmits(['connected', 'continue']);
const { t } = useI18n();

const POLL_MS = 3000;
const GIVE_UP_MS = 180000;

const qrcode = ref(props.initialQrcode);
const status = ref('connecting');
const phone = ref('');
const profile = ref('');
const error = ref('');
const expired = ref(false);
let timer = null;
let startedAt = 0;

const connected = computed(() => status.value === 'connected');

const stop = () => {
  clearInterval(timer);
  timer = null;
};

const apply = data => {
  status.value = data.status || 'connecting';
  if (data.qrcode) qrcode.value = data.qrcode;
  phone.value = data.phone_number || phone.value;
  profile.value = data.profile_name || profile.value;
};

const poll = async () => {
  if (Date.now() - startedAt > GIVE_UP_MS) {
    stop();
    expired.value = true;
    return;
  }
  try {
    const { data } = await UazapiAPI.status(props.inboxId);
    apply(data);
    if (data.connected) {
      stop();
      emit('connected', data);
    }
  } catch (e) {
    error.value = e?.response?.data?.error || e.message;
  }
};

const start = () => {
  stop();
  expired.value = false;
  error.value = '';
  startedAt = Date.now();
  timer = setInterval(poll, POLL_MS);
};

const requestQr = async () => {
  try {
    const { data } = await UazapiAPI.connect(props.inboxId);
    apply(data);
    start();
  } catch (e) {
    error.value = e?.response?.data?.error || e.message;
  }
};

onMounted(() => {
  if (qrcode.value) start();
  else requestQr();
});
onBeforeUnmount(stop);
</script>

<template>
  <div class="flex flex-col gap-4 max-w-lg">
    <h3 class="text-base font-medium text-n-slate-12">
      {{ t('INBOX_MGMT.UAZAPI.PANEL.TITLE') }}
    </h3>
    <div
      v-if="connected"
      class="rounded-xl border border-n-teal-6 bg-n-teal-3 p-4 text-n-teal-11"
    >
      {{
        phone
          ? t('INBOX_MGMT.UAZAPI.PANEL.CONNECTED_AS', {
              name: profile || '',
              phone,
            })
          : t('INBOX_MGMT.UAZAPI.PANEL.CONNECTED')
      }}
    </div>
    <template v-else>
      <ol class="list-decimal ps-5 text-sm text-n-slate-11 flex flex-col gap-1">
        <li>{{ t('INBOX_MGMT.UAZAPI.PANEL.STEP_1') }}</li>
        <li>{{ t('INBOX_MGMT.UAZAPI.PANEL.STEP_2') }}</li>
        <li>{{ t('INBOX_MGMT.UAZAPI.PANEL.STEP_3') }}</li>
      </ol>
      <div
        class="flex items-center justify-center rounded-xl border border-n-weak bg-white p-4 w-72 h-72"
      >
        <img
          v-if="qrcode && !expired"
          :src="qrcode"
          alt="QR code"
          class="w-64 h-64"
        />
        <span v-else-if="expired" class="text-sm text-n-slate-11 text-center">{{
          t('INBOX_MGMT.UAZAPI.PANEL.EXPIRED')
        }}</span>
        <span v-else class="text-sm text-n-slate-11">{{
          t('INBOX_MGMT.UAZAPI.PANEL.WAITING')
        }}</span>
      </div>
      <p v-if="!expired && qrcode" class="text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.UAZAPI.PANEL.WAITING') }}
      </p>
      <NextButton
        v-if="expired"
        :label="t('INBOX_MGMT.UAZAPI.PANEL.NEW_QR')"
        @click="requestQr"
      />
    </template>
    <p v-if="error" class="text-sm text-n-ruby-11">
      {{ t('INBOX_MGMT.UAZAPI.PANEL.ERROR', { message: error }) }}
    </p>
    <NextButton
      v-if="showContinue"
      :label="t('INBOX_MGMT.UAZAPI.PANEL.CONTINUE')"
      :variant="connected ? 'solid' : 'faded'"
      @click="emit('continue')"
    />
  </div>
</template>
